/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { debounce } from 'lodash';

import classNames from 'classnames';
import Th from '../Th';
import SummaryViewerReportModal from '../../modals/SummaryViewerReportModal';
import Navigation from '../Navigation';
import Breadcrumbs from '../Breadcrumbs';
import FilledButton from '../Buttons/FilledButton';
import CreateFolderMenu from './CreateFolderMenu';
import FolderRow from './FolderRow';
import MeetingRow from './MeetingRow';
import SubfolderWithPermissions from './SubfolderWithPermissions';
import MeetingDetailsModal from '../../modals/MeetingDetailsModal';
import ShareModal from '../../modals/ShareModal';
import FolderUsersModal from '../../modals/FolderUsersModal';
import MoveModal from '../../modals/MoveModal';
import ImportVideosModal from '../../modals/ImportVideosModal';
import importIcon from '../../assets/images/section-menu/import.svg';
import PermissionManager from '../PermissionManager';
import { RESOURCE_TYPES } from '../../services/permissions/constants';
import Layout from '../../containers/Layout';
import EmptyFolderMessage from './EmptyFolderMessage';
import Pagination from '../Pagination';

import copyFolderLink from '../../helpers/copyFolderLink';
import { UiContext } from '../../context/UiContext';
import useConfirmDelete from '../../hooks/useConfirmDelete';
import useUIHelper from '../../hooks/useUIHelper';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import UserService from '../../services/UserService';
import FolderService from '../../services/FolderService';
import classes from './styles.module.scss';
import downloadFolder from '../../helpers/downloadFolder';
import { ORG_ROLES } from '../../constants/main';
import { useUiStore } from '../../store';

const foldersThs = [
  {
    field: 'name',
    title: 'Name',
  },
  {
    field: 'ownerName',
    title: 'Owner',
  },
  {
    field: 'updatedAt',
    title: 'Modified Date',
  },
  {
    field: 'viewedMeetingCount',
    title: 'Videos Viewed',
  },
  {
    field: 'nope',
    title: 'Action',
    noSort: true,
    type: 'action',
  },
];

const meetingsThs = [
  {
    field: 'name',
    title: 'Name',
  },
  {
    field: 'ownerName',
    title: 'Owner',
  },
  {
    field: 'publishedByName',
    title: 'Published by',
    noSort: true,
  },
  {
    field: 'updatedAt',
    title: 'Modified Date',
  },
  {
    field: 'isViewed',
    title: 'Viewer Status',
  },
  {
    field: 'nope',
    title: 'Action',
    noSort: true,
  },
];

export default function Library({
  searchInput,
  setSearchInput,
  page,
  setPage,
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  debouncedSearchInput,
  refetchFolders,
  refetchFolder,
  areFoldersFetching,
  isFolderFetching,
  folders,
  openedFolder,
  id,
  isShare,
  itemsRange,
  deleteFolderMutation,
  moveToTrashMeetingMutation,
  updateFolderMutation,
  breadcrumbLabel,
  breadcrumbTo,
  deletedMeetings,
  restoreMeetingMutation,
  deleteMeetingMutation,
  moveFolderToTrashMutation,
  restoreFolderMutation,
  moveFolderMutation,
  moveMeetingMutation,
  meetingsPage,
  setMeetingsPage,
  onEmptyTrash,
  isEmptyTrashDisabled,
  emptyTrashLabel = 'Empty trash',
}) {
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [isCreateFolderMenuVisible, setIsCreateFolderMenuVisible] =
    useState(false);
  const [isShareFolderModalVisible, setIsShareFolderModalVisible] =
    useState(false);
  const [isMeetingDetailsModalVisible, setIsMeetingDetailsModal] =
    useState(false);
  const [isFolderUsersModalVisible, setIsFolderUsersModalVisible] =
    useState(false);
  const [isPermissionManagerVisible, setIsPermissionManagerVisible] =
    useState(false);
  const [permissionManagerResource, setPermissionManagerResource] =
    useState(null);
  const [permissionManagerType, setPermissionManagerType] = useState(null);

  const [
    isSummaryViewerReportModalVisible,
    setIsSummaryViewerReportModalVisible,
  ] = useState(false);
  const [currentReportData, setCurrentReportData] = useState(null);

  const isOnboardingVideoVisible = useUiStore((state) => state.isOnboardingVideoOpen);
  const setOnboardingVideoOpen = useUiStore((state) => state.setOnboardingVideoOpen);

  const isMobile = useCheckMobileScreen();
  const { showHelper, hideAllHelpers } = useUIHelper();
  const emptyMessageRef = useRef();

  // Move modal state
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [moveItemType, setMoveItemType] = useState(null);
  const [moveItemId, setMoveItemId] = useState(null);
  const [moveItemName, setMoveItemName] = useState(null);
  const [moveCurrentFolderId, setMoveCurrentFolderId] = useState(null);
  const [moveAllowedFolderIds, setMoveAllowedFolderIds] = useState(null);

  // Import existing videos state
  const [importFiles, setImportFiles] = useState(null);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importDefaultFolder, setImportDefaultFolder] = useState(null);
  const fileInputRef = useRef(null);

  const { notifySuccess, savedPageNumber, setSavedPageNumber } =
    useContext(UiContext);
  const navigate = useNavigate();

  const addButtonRef = useRef(null);

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );
  const isTrialPaywallOpen = useUiStore((state) => state.isTrialPaywallOpen);

  const { data: user } = useQuery('me', UserService.getMe);
  const isTrialPaywalled = user?.organizationBilling?.isPaywalled === true || isTrialPaywallOpen;

  const isFolderOwner = openedFolder?.data?.userRole === 'owner';
  const isFolderContributor = openedFolder?.data?.userRole === 'contributor';
  const isFolderOwnerOrContributor = isFolderOwner || isFolderContributor;

  const isTrashBin = breadcrumbTo === '/trash-bin';

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const isValidShareRootFolder =
      isShare === 'true' && id && openedFolder?.data.Parent === null;

    if (isValidShareRootFolder) {
      setIsShareFolderModalVisible(true);
      setSelectedFolder(openedFolder?.data);
    }
  }, [id, isShare, openedFolder?.data]);

  useEffect(() => {
    if (debouncedSearchInput) {
      if (id) {
        setMeetingsPage(1);
      } else {
        setPage(1);
      }
    }
  }, [debouncedSearchInput, id, setMeetingsPage, setPage]);

  useEffect(() => {
    if (savedPageNumber) {
      setPage(savedPageNumber);
    }

    return () => {
      if (id) {
        setSavedPageNumber(null);
      }
    };
  }, [id, savedPageNumber, setPage, setSavedPageNumber]);

  const switchPage = (pageNumber) => {
    if (id) {
      setMeetingsPage(pageNumber);
    } else {
      setPage(pageNumber);
    }
  };

  const handleDetailsMeeting = useCallback((meeting) => {
    setSelectedMeeting(meeting);
    setIsMeetingDetailsModal(true);
  }, []);

  const handleMoveFolderToTrash = useConfirmDelete(
    moveFolderToTrashMutation,
    'folder'
  );
  const handleRestoreFolder = (item) => restoreFolderMutation(item.id);
  const handleDeleteFolder = useConfirmDelete(deleteFolderMutation, 'folder', { isPermanent: true });
  const handleMoveMeetingToTrash = useConfirmDelete(
    moveToTrashMeetingMutation,
    'video'
  );
  const handleDeleteMeeting = useConfirmDelete(deleteMeetingMutation, 'video', { isPermanent: true });

  const handleShareFolder = useCallback((folder) => {
    setSelectedFolder(folder);
    setIsShareFolderModalVisible(true);
  }, []);

  const handleManagePermissions = useCallback((resource, type) => {
    setPermissionManagerResource(resource);
    setPermissionManagerType(type);
    setIsPermissionManagerVisible(true);
  }, []);

  // Keep handleManageFolderUsers for backward compatibility
  // It now uses PermissionManager internally
  const handleManageFolderUsers = useCallback((folder) => {
    handleManagePermissions(folder, RESOURCE_TYPES.FOLDER);
  }, [handleManagePermissions]);

  const handleManageMeetingAccess = useCallback((meeting) => {
    handleManagePermissions(meeting, RESOURCE_TYPES.MEETING);
  }, [handleManagePermissions]);

  const handleUpdateTitle = useCallback(
    (folderId, newTitle) => {
      const trimmedTitle = newTitle.trim();

      if (!trimmedTitle.length) {
        return;
      }

      updateFolderMutation({ folderId, name: trimmedTitle });
    },
    [updateFolderMutation]
  );

  const handleCopyFolderLink = useCallback(
    (folderId, meetingId) => {
      copyFolderLink(folderId, meetingId);
      notifySuccess(`Link copied to clipboard`);
    },
    [notifySuccess]
  );

  const showMeetingReport = useCallback((data) => {
    setCurrentReportData(data);
    setIsSummaryViewerReportModalVisible(true);
  }, []);

  const showFolderReport = (data) => {
    navigate(`/my-library/${data.id}/viewer-report`);
  };

  // Helper to get all nested folder IDs within a top-level folder
  const getAllNestedFolderIds = useCallback(async (folderId) => {
    const folderIds = [folderId];
    try {
      const folder = await FolderService.getFolder({
        id: folderId,
        page: 1,
        limit: 200,
      });
      const subfolders = folder?.subfolders || [];
      const nestedIdsArrays = await Promise.all(
        subfolders.map((subfolder) => getAllNestedFolderIds(subfolder.id))
      );
      nestedIdsArrays.forEach((nestedIds) => {
        folderIds.push(...nestedIds);
      });
    } catch (error) {
      console.error('Error fetching nested folders:', error);
    }
    return folderIds;
  }, []);

  // Helper to find top-level folder
  const findTopLevelFolderId = useCallback(async (folderId) => {
    try {
      const folder = await FolderService.getFolder({
        id: folderId,
        page: 1,
        limit: 1,
      });
      if (folder?.parentFolders && folder.parentFolders.length > 0) {
        // Get the root parent
        return folder.parentFolders[0].id;
      }
      return folderId; // Already top-level
    } catch (error) {
      console.error('Error finding top-level folder:', error);
      return folderId;
    }
  }, []);

  const handleMoveFolder = useCallback(
    async (folder) => {
      setMoveItemType('folder');
      setMoveItemId(folder.id);
      setMoveItemName(folder.name);
      setMoveCurrentFolderId(folder.parentId);

      // Calculate allowed folder IDs for folder owners
      if (user?.orgRole?.access !== ORG_ROLES.OWNER) {
        // For folder owners, get all nested folders within the same top-level folder
        const topLevelFolderId = await findTopLevelFolderId(folder.id);
        const allowedIds = await getAllNestedFolderIds(topLevelFolderId);
        setMoveAllowedFolderIds(allowedIds);
      } else {
        // Org owners can move anywhere
        setMoveAllowedFolderIds(null);
      }

      setIsMoveModalOpen(true);
    },
    [user, getAllNestedFolderIds, findTopLevelFolderId]
  );

  const handleMoveMeeting = useCallback(
    async (meeting) => {
      setMoveItemType('meeting');
      setMoveItemId(meeting.id);
      setMoveItemName(meeting.title);
      setMoveCurrentFolderId(meeting.folderId);

      // Calculate allowed folder IDs for folder owners
      if (user?.orgRole?.access !== ORG_ROLES.OWNER && meeting.folderId) {
        // For folder owners, get all nested folders within the same top-level folder
        const topLevelFolderId = await findTopLevelFolderId(meeting.folderId);
        const allowedIds = await getAllNestedFolderIds(topLevelFolderId);
        setMoveAllowedFolderIds(allowedIds);
      } else {
        // Org owners can move anywhere
        setMoveAllowedFolderIds(null);
      }

      setIsMoveModalOpen(true);
    },
    [user, getAllNestedFolderIds, findTopLevelFolderId]
  );

  const handleMoveConfirm = useCallback(
    async (destinationFolderId, callbacks = {}) => {
      // For folder owners, destinationFolderId should never be null
      if (user?.orgRole?.access !== ORG_ROLES.OWNER && destinationFolderId === null) {
        return;
      }
      
      try {
        if (moveItemType === 'folder') {
          await moveFolderMutation({ folderId: moveItemId, destinationFolderId });
        } else if (moveItemType === 'meeting') {
          await moveMeetingMutation({ meetingId: moveItemId, destinationFolderId });
        }
        // Only show success and close modal if mutation succeeds
        notifySuccess(
          `${moveItemType === 'folder' ? 'Folder' : 'Meeting'} moved successfully.`
        );
        setIsMoveModalOpen(false);
        // Reset state
        setMoveItemType(null);
        setMoveItemId(null);
        setMoveItemName(null);
        setMoveCurrentFolderId(null);
        setMoveAllowedFolderIds(null);
        // Call custom onSuccess if provided
        if (callbacks.onSuccess) {
          callbacks.onSuccess();
        }
      } catch (error) {
        // Error notification is already handled by mutation's onError
        // Call custom onError if provided
        if (callbacks.onError) {
          callbacks.onError();
        }
        // Re-throw to let MoveModal handle the loading state
        throw error;
      }
    },
    [
      moveItemType,
      moveItemId,
      moveFolderMutation,
      moveMeetingMutation,
      notifySuccess,
    ]
  );

  const handleMoveCancel = useCallback(() => {
    setIsMoveModalOpen(false);
    setMoveItemType(null);
    setMoveItemId(null);
    setMoveItemName(null);
    setMoveCurrentFolderId(null);
    setMoveAllowedFolderIds(null);
  }, []);

  const handleImportExistingVideos = useCallback(() => {
    if (isMobile) return;
    const folder = openedFolder?.data;
    setImportDefaultFolder(
      folder ? { id: folder.id, name: folder.name } : null
    );
    fileInputRef.current?.click();
  }, [isMobile, openedFolder]);

  const handleFileInputChange = useCallback((e) => {
    const { files } = e.target;
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    e.target.value = '';
    setImportFiles(fileArray);
    setIsImportModalVisible(true);
  }, []);

  const isLoading = areFoldersFetching || isFolderFetching;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const breadcrumbsOptions = [{ label: breadcrumbLabel, to: breadcrumbTo }];

  let subfoldersContent;
  let tableContent;

  if (folders) {
    tableContent = (folders.rows || []).map((fdr) => (
      <FolderRow
        key={fdr.id}
        onClick={debounce(
          () => {
            if (isTrashBin) {
              return;
            }

            // Check if the current URL already contains fdr.id
            if (window.location.pathname.includes(`${fdr.id}`)) {
              return;
            }

            navigate(`${fdr.id}`);
            setSavedPageNumber(page);
          },
          300,
          {
            leading: true,
            trailing: false,
          }
        )}
        classes={classes}
        folder={fdr}
        user={user}
        onUpdateTitle={(newTitle) => handleUpdateTitle(fdr.id, newTitle)}
        handleDelete={() => handleDeleteFolder(fdr)}
        handleShare={() => handleShareFolder(fdr)}
        handleMoveToTrash={() => handleMoveFolderToTrash(fdr)}
        handleRestore={() => handleRestoreFolder(fdr)}
        handleManageUsers={() => handleManageFolderUsers(fdr)}
        handleManageAccess={handleManageFolderUsers}
        handleCopyLink={() => handleCopyFolderLink(fdr.id)}
        handleMove={handleMoveFolder}
        showReport={showFolderReport}
        isTrashBin={isTrashBin}
        viewedMeetingCount={fdr.viewedMeetingCount}
        meetingCount={fdr.meetingCount}
      />
    ));

    if (isTrashBin && deletedMeetings?.length) {
      const deletedMeetingsContent = deletedMeetings.map((meeting) => (
        <MeetingRow
          key={meeting.id}
          classes={classes}
          meeting={meeting}
          folderUserRole={openedFolder?.data?.userRole}
          user={user}
          handleDetails={() => handleDetailsMeeting(meeting)}
          handleDelete={() => handleDeleteMeeting(meeting)}
          handleMoveToTrash={() => handleMoveMeetingToTrash(meeting)}
          handleRestore={() => restoreMeetingMutation(meeting.id)}
          handleCopyLink={() =>
            handleCopyFolderLink(meeting.folderId, meeting.id)
          }
          refetchFolder={refetchFolder}
          showReport={showMeetingReport}
          isTrashBin
        />
      ));

      tableContent = [...tableContent, ...deletedMeetingsContent];
    }
  }

  if (openedFolder) {
    const userRole = openedFolder?.data?.userRole;

    subfoldersContent = (openedFolder.subfolders || []).map((subfolder) => (
        <li className={classes.subfolderContainer} key={subfolder.id}>
        <SubfolderWithPermissions
          subfolder={subfolder}
          user={user}
          userRole={userRole}
          navigate={navigate}
          breadcrumbTo={breadcrumbTo}
          handleUpdateTitle={handleUpdateTitle}
          handleShareFolder={handleShareFolder}
          handleCopyFolderLink={handleCopyFolderLink}
          downloadFolder={downloadFolder}
          handleDeleteFolder={handleDeleteFolder}
          handleMoveFolderToTrash={handleMoveFolderToTrash}
          handleRestoreFolder={handleRestoreFolder}
          handleManageFolderUsers={handleManageFolderUsers}
          handleMove={handleMoveFolder}
          />
        </li>
    ));

    tableContent = (openedFolder.meetings || []).map((meeting) => (
      <MeetingRow
        key={meeting.id}
        classes={classes}
        meeting={meeting}
        folderUserRole={openedFolder?.data?.userRole}
        user={user}
        handleDetails={() => handleDetailsMeeting(meeting)}
        handleDelete={() => handleDeleteMeeting(meeting)}
        handleMoveToTrash={() => handleMoveMeetingToTrash(meeting)}
        handleCopyLink={() =>
          handleCopyFolderLink(meeting.folderId, meeting.id)
        }
        refetchFolder={refetchFolder}
        showReport={showMeetingReport}
        handleManageAccess={handleManageMeetingAccess}
        handleMove={handleMoveMeeting}
      />
    ));

    const formattedBreadcrumbs = (openedFolder.parentFolders || []).map((folder) => ({
      label: folder.name,
      to: `${breadcrumbTo}/${folder.id}`,
    }));

    breadcrumbsOptions.push(...formattedBreadcrumbs);
  }

  let isEmpty = id
    ? !(openedFolder?.subfolders?.length) && !(openedFolder?.meetings?.length)
    : !folders?.rows.length;

  if (isTrashBin) {
    isEmpty = !!(!deletedMeetings?.length && !folders?.rows.length);
  }

  const currentThs = id ? meetingsThs : foldersThs;

  useEffect(() => {
    setBreadcrumbsOptions(breadcrumbsOptions);
  }, [breadcrumbsOptions, setBreadcrumbsOptions]);

  useEffect(() => {
    if (isMobile) {
      hideAllHelpers();
    }
  }, [isMobile, hideAllHelpers]);

  useEffect(() => () => hideAllHelpers(), [hideAllHelpers]);

  // Show onboarding video modal for first-time users with empty library
  useEffect(() => {
    const shouldShowOnboarding =
      !isLoading &&
      isEmpty &&
      !isMobile &&
      !isTrialPaywalled &&
      !debouncedSearchInput &&
      !id &&
      localStorage.getItem('onboardingVideoSeen') !== 'true';

    if (shouldShowOnboarding) {
      setOnboardingVideoOpen(true);
    }
  }, [isLoading, isEmpty, isMobile, isTrialPaywalled, debouncedSearchInput, id, setOnboardingVideoOpen]);

  // Show helper for Add New button when library is empty
  useEffect(() => {
    const shouldShowHelper =
      !isLoading &&
      isEmpty &&
      !isMobile &&
      !isTrialPaywalled &&
      !isOnboardingVideoVisible &&
      !debouncedSearchInput &&
      !id && // Only for main library, not subfolders
      addButtonRef.current; // Make sure Add New button exists

    if (shouldShowHelper) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
                  showHelper({
                    ref: addButtonRef,
                    position: 'left',
                    fontSize: 17,
                    content: (
            <div style={{ maxWidth: '300px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#111111', fontSize: '19px' }}>
                Get Started!
              </h4>
              <p style={{ margin: '0 0 12px 0', color: '#666666', fontSize: '17px', lineHeight: '1.4' }}>
                Your library is empty. Click &quot;Add New&quot; to create your first folder and start organizing your content.
              </p>
              <div style={{ fontSize: '14px', color: '#f8585c', fontWeight: '500' }}>
                💡 Tip: You can create folders to organize your meetings and files
              </div>
            </div>
          ),
          backdrop: 'blur',
          arrow: true,
          offset: 12,
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isLoading, isEmpty, isMobile, isTrialPaywalled, isOnboardingVideoVisible, debouncedSearchInput, id, showHelper]);

  // Show helper for Add New button when inside a folder
  useEffect(() => {
    const shouldShowHelper =
      !isLoading &&
      id && // Only when inside a folder (id exists)
      isEmpty && // Only when the folder has no content
      !isMobile &&
      !isTrialPaywalled &&
      addButtonRef.current; // Make sure Add New button exists

    if (shouldShowHelper) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        showHelper({
          ref: addButtonRef,
          position: 'left',
          fontSize: 17,
          content: (
            <div style={{ maxWidth: '300px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#111111', fontSize: '19px' }}>
                Add Content
              </h4>
              <p style={{ margin: '0 0 12px 0', color: '#666666', fontSize: '17px', lineHeight: '1.4' }}>
                Click &quot;Add New&quot; to create sub-folders or create videos to organize your content better.
              </p>
              <div style={{ fontSize: '14px', color: '#f8585c', fontWeight: '500' }}>
                💡 You can create folders within folders for better organization
              </div>
            </div>
          ),
          backdrop: 'blur',
          arrow: true,
          offset: 12,
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isLoading, isEmpty, isMobile, isTrialPaywalled, id, showHelper]);

  return (
    <Layout
      hasUserBlock
      hasSearch
      searchInput={searchInput}
      setSearchInput={setSearchInput}
    >
      <div className={classes.Library}>
        {!isTrashBin && <Navigation />}
        <div className={classes.container}>
          <div
            className={classNames(classes.libraryHeader, {
              [classes.trash]: isTrashBin,
            })}
          >
            <div>
              <Breadcrumbs breadcrumbsOptions={breadcrumbsOptions} />
              {/* {openedFolder && <h2>{openedFolder.data.name}</h2>} */}
              {isTrashBin && (
                <span className={classes.warning}>
                  All files and folders permanently deleted after 30 days
                </span>
              )}
            </div>

            <div className={classes.buttonsContainer}>
              {isTrashBin && onEmptyTrash && user?.orgRole?.access !== ORG_ROLES.VIEWER && (
                <FilledButton
                  onClick={onEmptyTrash}
                  width={196}
                  isDisabled={isEmptyTrashDisabled}
                  data-testid="empty-trash-button"
                >
                  {emptyTrashLabel}
                </FilledButton>
              )}
              {(user?.orgRole?.access !== ORG_ROLES.VIEWER ||
                isFolderOwnerOrContributor) &&
                !isTrashBin && (
                  <>
                    <div ref={addButtonRef} style={{ position: 'relative' }}>
                      <FilledButton
                        onClick={() =>
                          setIsCreateFolderMenuVisible((prevState) => !prevState)
                        }
                        width={196}
                      >
                        + Add new
                      </FilledButton>
                      {isCreateFolderMenuVisible && (
                        <CreateFolderMenu
                          setIsVisible={setIsCreateFolderMenuVisible}
                          refetchFolders={refetchFolders}
                          refetchFolder={refetchFolder}
                          addButtonRef={addButtonRef}
                          folder={openedFolder?.data}
                          user={user}
                        />
                      )}
                    </div>
                    {!isMobile && (
                      <button
                        type="button"
                        className={classes.importButton}
                        onClick={handleImportExistingVideos}
                      >
                        <img src={importIcon} alt="" />
                        Import videos
                      </button>
                    )}
                  </>
                )}
            </div>
          </div>
          {!!subfoldersContent?.length && (
            <ul className={classes.subfolders}>{subfoldersContent}</ul>
          )}
          <div className={classes.tableContainer}>
            <table className={classes.table}>
              <thead>
                <tr>
                  {currentThs.map((th) => (
                    <Th
                      key={th.field}
                      classes={classes}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      setSortField={setSortField}
                      setSortOrder={setSortOrder}
                      field={th.field}
                      searchInput={searchInput}
                      setSearchInput={setSearchInput}
                      title={th.title}
                      noSort={th.noSort}
                    />
                  ))}
                </tr>
              </thead>
              {!!tableContent?.length && (
                <tbody>{tableContent}</tbody>
              )}
            </table>
          </div>

          {!isLoading && isEmpty && (
            <button
              ref={emptyMessageRef}
              className={classes.empty}
              onClick={() => {
                if (isTrialPaywalled) return;
                if (isMobile) return;
                if (!debouncedSearchInput && !id && addButtonRef.current) {
                  showHelper({
                    ref: addButtonRef,
                    position: 'left',
                    fontSize: 17,
                    content: (
                      <div style={{ maxWidth: '300px' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#111111', fontSize: '19px' }}>
                          Get Started!
                        </h4>
                        <p style={{ margin: '0 0 12px 0', color: '#666666', fontSize: '17px', lineHeight: '1.4' }}>
                          Your library is empty. Click &quot;Add New&quot; to create your first folder and start organizing your content.
                        </p>
                        <div style={{ fontSize: '14px', color: '#f8585c', fontWeight: '500' }}>
                          💡 Tip: You can create folders to organize your meetings and files
                        </div>
                      </div>
                    ),
                    backdrop: 'blur',
                    arrow: true,
                    offset: 12,
                  });
                }
              }}
              style={{
                cursor: !debouncedSearchInput && !id ? 'pointer' : 'default',
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit',
                textAlign: 'center'
              }}
              type="button"
            >
              {debouncedSearchInput ? (
                "Oops! We can't find any files with that name."
              ) : (
                <EmptyFolderMessage />
              )}
            </button>
          )}

          {((folders?.count > itemsRange && !id) ||
            openedFolder?.count > itemsRange) && (
            <Pagination
              activePage={id ? meetingsPage : page}
              itemsCountPerPage={itemsRange}
              totalItemsCount={id ? openedFolder?.count : folders?.count}
              pageRangeDisplayed={itemsRange}
              onChange={switchPage}
            />
          )}
        </div>

        <MeetingDetailsModal
          meeting={selectedMeeting}
          show={isMeetingDetailsModalVisible}
          handleClose={() => setIsMeetingDetailsModal(false)}
        />
        <ShareModal
          type={selectedFolder?.parentId ? 'subfolder' : 'folder'}
          data={selectedFolder}
          show={isShareFolderModalVisible}
          handleClose={() => setIsShareFolderModalVisible(false)}
        />
        <FolderUsersModal
          folder={selectedFolder}
          show={isFolderUsersModalVisible}
          handleClose={() => setIsFolderUsersModalVisible(false)}
          handleShare={handleShareFolder}
          user={user}
          refetchFolders={refetchFolders}
        />
        <PermissionManager
          show={isPermissionManagerVisible}
          onClose={() => {
            setIsPermissionManagerVisible(false);
            setPermissionManagerResource(null);
            setPermissionManagerType(null);
          }}
          resourceId={permissionManagerResource?.id}
          resourceType={permissionManagerType}
          resourceName={
            permissionManagerResource?.name || permissionManagerResource?.title
          }
          user={user}
          folderId={
            permissionManagerType === RESOURCE_TYPES.MEETING
              ? permissionManagerResource?.folderId
              : undefined
          }
          folderUserRole={
            permissionManagerType === RESOURCE_TYPES.MEETING && openedFolder?.data?.userRole
              ? openedFolder.data.userRole
              : undefined
          }
          onResourceUpdate={() => {
            // Refetch folder data after ownership transfer
            if (permissionManagerType === RESOURCE_TYPES.FOLDER) {
              // Only refetch the appropriate query based on current view
              if (id) {
                // We're viewing a folder, refetch it
                // If user loses access, the query will fail and navigate to access request page
                refetchFolder().catch(() => {
                  // If refetch fails (403), navigate will be handled by query onError
                  // But we should also refetch the root list in case user lost access
                  refetchFolders();
                });
              } else {
                // We're viewing the root list, refetch it
                refetchFolders();
              }
            } else if (permissionManagerType === RESOURCE_TYPES.MEETING) {
              // Refetch folder to update meeting permissions
              if (id) {
                refetchFolder();
              }
            }
          }}
        />
        <SummaryViewerReportModal
          show={isSummaryViewerReportModalVisible}
          handleClose={() => setIsSummaryViewerReportModalVisible(false)}
          contentType="meeting"
          contentTitle={currentReportData?.name || currentReportData?.title}
          id={currentReportData?.id}
        />
        <MoveModal
          isOpen={isMoveModalOpen}
          onClose={handleMoveCancel}
          itemType={moveItemType}
          itemId={moveItemId}
          itemName={moveItemName}
          currentFolderId={moveCurrentFolderId}
          allowedFolderIds={moveAllowedFolderIds}
          onMove={handleMoveConfirm}
          isOrgOwner={user?.orgRole?.access === ORG_ROLES.OWNER}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        <ImportVideosModal
          show={isImportModalVisible}
          onHide={() => {
            setIsImportModalVisible(false);
            setImportFiles(null);
          }}
          files={importFiles}
          defaultFolder={importDefaultFolder}
          fileInputRef={fileInputRef}
        />
      </div>
    </Layout>
  );
}
